import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface BranchWiseCumulativeProps {
  heading: string;
  rowData: any[];
  yr1Label?: string;
  yr2Label?: string;
}

const FY_ORDER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const MONTH_LABEL: Record<number, string> = {
  4: "Apr", 5: "May", 6: "Jun", 7: "Jul", 8: "Aug", 9: "Sep",
  10: "Oct", 11: "Nov", 12: "Dec", 1: "Jan", 2: "Feb", 3: "Mar",
};

const BRANCH_COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#d97706",
  "#7c3aed", "#db2777", "#0891b2", "#65a30d", "#f97316",
];

const formatAmount = (val: number) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const BranchWiseCumulative: React.FC<BranchWiseCumulativeProps> = ({
  heading,
  rowData,
  yr1Label: yr1Prop,
  yr2Label: yr2Prop,
}) => {
  const yr1Label = yr1Prop || "";
  const yr2Label = yr2Prop || "";

  const branches = Array.isArray(rowData) ? rowData : [];
  const [selectedIdx, setSelectedIdx] = useState(0);

  const selectedBranch = branches[selectedIdx];
  const selectedColor = BRANCH_COLORS[selectedIdx % BRANCH_COLORS.length];

  const MONTH_NAME_NUM: Record<string, number> = {
    April: 4, May: 5, June: 6, July: 7, August: 8, September: 9,
    October: 10, November: 11, December: 12, January: 1, February: 2, March: 3,
  };

  const chartData = useMemo(() => {
    if (!selectedBranch) return [];

    // Old format: { Particular, DataM: [{Month(name), Bold, CYAmount, LYAmount}] }
    if (selectedBranch.DataM !== undefined) {
      const monthRows = (selectedBranch.DataM as any[]).filter((d) => d.Bold === 0);
      let cumCY = 0, cumLY = 0, lastCYRowIdx = -1;
      const points = monthRows.map((d, i) => {
        cumCY += d.CYAmount || 0;
        cumLY += d.LYAmount || 0;
        if ((d.CYAmount || 0) !== 0) lastCYRowIdx = i;
        const mNum = MONTH_NAME_NUM[d.Month as string];
        return { i, label: MONTH_LABEL[mNum] || (d.Month as string).substring(0, 3), cumCY, cumLY };
      });
      return points.map((p) => ({
        Particulars: p.label,
        CumulativeCY: p.i <= lastCYRowIdx ? p.cumCY : null,
        CumulativeLY: p.cumLY,
      }));
    }

    // New format: { Particulars, CYData, LYData }
    const cyMap = new Map<number, number>();
    (selectedBranch.CYData || []).forEach((d: any) => cyMap.set(d.Month, d.CumulativeCY));

    const lyMap = new Map<number, number>();
    (selectedBranch.LYData || []).forEach((d: any) => lyMap.set(d.Month, d.CumulativeLY));

    const lyMonthSet = new Set<number>();
    (selectedBranch.LYData || []).forEach((d: any) => lyMonthSet.add(d.Month));
    const months = FY_ORDER.filter((m) => lyMonthSet.has(m));

    const lastCYFYIdx =
      cyMap.size > 0
        ? Math.max(...Array.from(cyMap.keys()).map((m) => FY_ORDER.indexOf(m)))
        : -1;

    const carryForwardCY = (month: number): number | null => {
      const idx = FY_ORDER.indexOf(month);
      if (idx > lastCYFYIdx) return null;
      for (let i = idx; i >= 0; i--) {
        if (cyMap.has(FY_ORDER[i])) return cyMap.get(FY_ORDER[i])!;
      }
      return null;
    };

    return months.map((month) => ({
      Particulars: MONTH_LABEL[month],
      CumulativeCY: carryForwardCY(month),
      CumulativeLY: lyMap.get(month) ?? 0,
    }));
  }, [selectedBranch]);

  const maxYValue = useMemo(() => {
    if (!chartData.length) return 0;
    const all = chartData.flatMap((d) => [d.CumulativeCY ?? 0, d.CumulativeLY ?? 0]);
    return Math.max(...all);
  }, [chartData]);

  const yAxisTicks = useMemo(() => {
    if (!maxYValue) return [];
    const step = maxYValue / 5;
    return [0, 1, 2, 3, 4, 5].map((i) => Math.round(step * i));
  }, [maxYValue]);

  // Summary totals for selected branch
  const branchName = (b: any) => b?.Particulars || b?.Particular || "";

  const summary = useMemo(() => {
    if (!selectedBranch) return { cyTotal: 0, lyTotal: 0, growth: 0 };

    let cyTotal = 0, lyTotal = 0;
    if (selectedBranch.DataM !== undefined) {
      const monthRows = (selectedBranch.DataM as any[]).filter((d) => d.Bold === 0);
      cyTotal = monthRows.reduce((s, d) => s + (d.CYAmount || 0), 0);
      lyTotal = monthRows.reduce((s, d) => s + (d.LYAmount || 0), 0);
    } else {
      const last = (arr: any[]) => arr[arr.length - 1];
      cyTotal = last(selectedBranch.CYData || [])?.CumulativeCY ?? 0;
      lyTotal = last(selectedBranch.LYData || [])?.CumulativeLY ?? 0;
    }
    const growth = lyTotal > 0 ? ((cyTotal - lyTotal) / lyTotal) * 100 : 0;
    return { cyTotal, lyTotal, growth };
  }, [selectedBranch]);

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "14px",
        border: "1px solid #dbe3ef",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(15,23,42,0.09)",
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "13px 16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>
          {heading} Cumulative
        </h3>
      </div>

      {/* Branch Selector */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          padding: "12px 16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#fafbfc",
        }}
      >
        {branches.map((b, i) => {
          const color = BRANCH_COLORS[i % BRANCH_COLORS.length];
          const active = selectedIdx === i;
          return (
            <button
              key={i}
              onClick={() => setSelectedIdx(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 13px",
                borderRadius: "20px",
                border: `1.5px solid ${color}`,
                background: active ? color : "#ffffff",
                color: active ? "#ffffff" : color,
                fontWeight: 600,
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.15s ease",
                boxShadow: active ? `0 2px 8px ${color}40` : "none",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: active ? "#ffffff" : color,
                  flexShrink: 0,
                }}
              />
              {branchName(b)}
            </button>
          );
        })}
      </div>

      {/* Summary Cards */}
      {selectedBranch && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          {[
            { label: `FY ${yr2Label} (Cumulative)`, value: formatAmount(summary.cyTotal), color: selectedColor },
            { label: `FY ${yr1Label} (Cumulative)`, value: formatAmount(summary.lyTotal), color: "#3b82f6" },
            {
              label: "Growth %",
              value: `${summary.growth > 0 ? "▲ " : summary.growth < 0 ? "▼ " : ""}${Math.abs(summary.growth).toFixed(2)}%`,
              color: summary.growth > 0 ? "#16a34a" : summary.growth < 0 ? "#dc2626" : "#94a3b8",
            },
          ].map((card, i) => (
            <div
              key={i}
              style={{
                padding: "12px 16px",
                borderRight: i < 2 ? "1px solid #e2e8f0" : "none",
                background: "#ffffff",
              }}
            >
              <div style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 600, letterSpacing: "0.3px", marginBottom: "4px", textTransform: "uppercase" }}>
                {card.label}
              </div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: card.color }}>
                {card.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      <div
        style={{
          height: "460px",
          padding: "20px 20px 12px",
          background: "#f8fafc",
        }}
      >
        {!chartData.length ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: "14px",
            }}
          >
            No data for {branchName(selectedBranch)}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 40, left: 20, bottom: 70 }}
            >
              <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />

              <XAxis
                dataKey="Particulars"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={75}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />

              <YAxis
                domain={[0, maxYValue]}
                ticks={yAxisTicks}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 10000000
                    ? `${(v / 10000000).toFixed(1)}Cr`
                    : v >= 100000
                    ? `${(v / 100000).toFixed(1)}L`
                    : v.toLocaleString("en-IN")
                }
              />

              <Tooltip
                content={({ active, payload, label }: any) => {
                  if (!active || !payload?.length) return null;
                  const cyVal = payload.find((p: any) => p.dataKey === "CumulativeCY")?.value ?? null;
                  const lyVal = payload.find((p: any) => p.dataKey === "CumulativeLY")?.value ?? 0;
                  const growthPer =
                    cyVal !== null && lyVal > 0
                      ? ((cyVal - lyVal) / lyVal) * 100
                      : null;
                  const growthColor =
                    growthPer === null ? "#94a3b8" : growthPer > 0 ? "#059669" : "#ef4444";

                  return (
                    <div
                      style={{
                        background: "#ffffff",
                        border: `1px solid ${selectedColor}40`,
                        borderTop: `3px solid ${selectedColor}`,
                        borderRadius: "10px",
                        padding: "10px 14px",
                        boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
                        fontSize: "13px",
                        minWidth: "200px",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 8px",
                          fontWeight: 700,
                          color: "#0f172a",
                          borderBottom: "1px solid #e2e8f0",
                          paddingBottom: "6px",
                        }}
                      >
                        {branchName(selectedBranch)} — {label}
                      </p>
                      <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                        <span style={{ color: "#3b82f6", fontWeight: 600 }}>FY {yr1Label}</span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>{formatAmount(lyVal)}</span>
                      </div>
                      {cyVal !== null && (
                        <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
                          <span style={{ color: selectedColor, fontWeight: 600 }}>FY {yr2Label}</span>
                          <span style={{ fontWeight: 700, color: "#0f172a" }}>{formatAmount(cyVal)}</span>
                        </div>
                      )}
                      {growthPer !== null && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            margin: "6px 0 0",
                            paddingTop: "6px",
                            borderTop: "1px solid #e2e8f0",
                          }}
                        >
                          <span style={{ color: "#64748b", fontWeight: 600 }}>Growth %</span>
                          <span style={{ fontWeight: 700, color: growthColor }}>
                            {growthPer > 0 ? "▲ " : "▼ "}{Math.abs(growthPer).toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }}
              />

              <Legend
                wrapperStyle={{ paddingTop: "12px", fontSize: "12px" }}
                iconType="circle"
              />

              {/* LY — blue dashed */}
              <Line
                type="monotone"
                dataKey="CumulativeLY"
                name={`FY ${yr1Label}`}
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
                connectNulls={false}
                activeDot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
              />

              {/* CY — branch color solid */}
              <Line
                type="monotone"
                dataKey="CumulativeCY"
                name={`FY ${yr2Label}`}
                stroke={selectedColor}
                strokeWidth={3}
                dot={false}
                connectNulls={false}
                activeDot={{ r: 5, fill: selectedColor, stroke: "#fff", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BranchWiseCumulative;
